import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import { IAddDeviceRequest } from "../../../../../../@types/Devices";
import { Notification } from "../../../../../../@types/Notification";
import { Button } from "../../../../../../components/Button";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../components/TextField";
import { devicesService } from "../../../../../../services/devicesService";
import {
  AddDeviceCancelButton,
  AddDeviceTitle,
  ErrorMessage,
  inputCss,
  InputRow,
  InputsContainer,
  AddDeviceModalContainer,
  ReassignmentMessage,
  SubmitContainer,
} from "./AddDeviceModal.styles";

// EN-5094: returned by POST /Portal/Devices when the serial is still registered
// to a patient outside the caller's wards; the staff member must confirm before
// the reassignment is completed.
const DEVICE_REASSIGNMENT_CONFIRMATION_REQUIRED =
  "DeviceReassignmentConfirmationRequired";

interface IAddDeviceModalProps {
  patientId: number;
  onClose: (notification?: Notification, refetchTable?: boolean) => void;
}

export function AddDeviceModal({ patientId, onClose }: IAddDeviceModalProps) {
  const { t } = useTranslation();

  const [typesOptions, setTypesOptions] = useState<IOption[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [identifierError, setIdentifierError] = useState("");
  const [reassignmentMessage, setReassignmentMessage] = useState("");

  const identifierRef = useRef<HTMLDivElement>(null);

  const formValidationSchema = yup.object().shape({
    deviceTypeId: yup
      .string()
      .required(t("patientPage_devices_add_typeRequired")),
    identifier: yup
      .string()
      .test("number", t("patientPage_devices_add_identifierInvalid"), (val) => {
        if (val === undefined) return true;
        const reg = /^\d+$/;
        return reg.test(val);
      })
      .test("len", t("patientPage_devices_add_exactly15"), (val) => {
        if (val === undefined) return true;
        return val.length === 15;
      })
      .required(t("patientPage_devices_add_identifierRequired")),
  });

  const submitDevice = async (
    { deviceTypeId, identifier, patientId }: IAddDeviceRequest,
    confirmReassignment: boolean
  ) => {
    setIsLoading(true);
    setIdentifierError("");

    const request: IAddDeviceRequest = {
      deviceTypeId,
      identifier,
      patientId,
      confirmReassignment,
    };

    const result = await devicesService.addDevice(request);

    setIsLoading(false);

    const notification: Notification = {
      show: true,
      message: "",
      type: "success",
      width: "max-content",
    };

    let refetchTable = false;

    if (result.status >= 200 && result.status < 300) {
      notification.message = t("patientPage_devices_add_added");
      refetchTable = true;
    } else {
      if (result.status === 500) {
        const error = result.data.errors[0];

        if (error.code === DEVICE_REASSIGNMENT_CONFIRMATION_REQUIRED) {
          setReassignmentMessage(error.message);
          return;
        }

        setReassignmentMessage("");
        setIdentifierError(error.message);
        return;
      }

      notification.message = t("patientPage_devices_add_failed");
      notification.type = "error";
    }

    onClose(notification, refetchTable);
  };

  const handleOnSubmitForm = async (values: IAddDeviceRequest) => {
    await submitDevice(values, false);
  };

  const formik = useFormik({
    initialValues: {
      deviceTypeId: undefined,
      identifier: "",
      patientId,
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  useEffect(() => {
    devicesService.getDeviceTypes().then((types) => {
      if (types === undefined) return;

      const tempTypes: IOption[] = [];

      types.data.forEach((type) => {
        tempTypes.push({
          label: type.name,
          value: type.id,
          key: uuidv4(),
        });
      });

      setTypesOptions(tempTypes);
    });
  }, []);

  const shouldShowErrorMessage = (
    field: "deviceTypeId" | "identifier"
  ): string => {
    let error = "";

    if (formik.errors[field] !== undefined) {
      error = ((formik.errors[field] as string) ?? "").toString();
    }
    return formik.touched[field] ? error : "";
  };

  useEffect(() => {
    formik.setFieldError("identifier", identifierError);
  }, [identifierError]);

  if (reassignmentMessage !== "") {
    return (
      <AddDeviceModalContainer>
        <AddDeviceTitle>
          <p>{t("patientPage_devices_add_title")}</p>
        </AddDeviceTitle>
        <InputsContainer>
          <ReassignmentMessage>{reassignmentMessage}</ReassignmentMessage>
        </InputsContainer>
        <SubmitContainer>
          <Button
            type="button"
            label={t("patientPage_devices_add_registerYes")}
            isLoading={isLoading}
            onClick={() => submitDevice(formik.values, true)}
          />
          <AddDeviceCancelButton onClick={() => setReassignmentMessage("")}>
            {t("patientPage_devices_goBack")}
          </AddDeviceCancelButton>
        </SubmitContainer>
      </AddDeviceModalContainer>
    );
  }

  return (
    <AddDeviceModalContainer>
      <AddDeviceTitle>
        <p>{t("patientPage_devices_add_title")}</p>
      </AddDeviceTitle>
      <InputsContainer>
        <InputRow>
          <p>{t("patientPage_devices_type")}</p>
          <SelectDropdown
            placeholder={t("patientPage_devices_add_select")}
            options={typesOptions}
            width={225}
            height={42}
            value={formik.values.deviceTypeId}
            onValueChange={(value) => {
              formik.setFieldValue("deviceTypeId", value.toString());
              identifierRef.current?.focus();
            }}
          />

          <ErrorMessage>{shouldShowErrorMessage("deviceTypeId")}</ErrorMessage>
        </InputRow>
        <InputRow>
          <p> ID </p>
          <TextField
            name="identifier"
            placeholder={t("patientPage_devices_add_identifier")}
            label=""
            backgroudColor="white"
            className={inputCss()}
            onChange={formik.handleChange}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                formik.submitForm();
              }
            }}
            errorMessage={shouldShowErrorMessage("identifier")}
            maxLength={15}
            innerRef={identifierRef}
          />
        </InputRow>
      </InputsContainer>
      <SubmitContainer>
        <Button
          type="button"
          label={t("patientPage_devices_add_confirm")}
          isLoading={isLoading}
          onClick={formik.submitForm}
        />
        <AddDeviceCancelButton onClick={() => onClose()}>
          {t("common_cancel")}
        </AddDeviceCancelButton>
      </SubmitContainer>
    </AddDeviceModalContainer>
  );
}
